import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Stack,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  useBreakpointValue,
  BoxProps,
} from '@chakra-ui/react';

const MotionBox = motion(Box);

export interface StepWrapperProps extends BoxProps {
  stepKey: string;
  icon: React.ElementType;
  title: string;
  description?: string;
  extraText?: string;
  children: React.ReactNode;
}

export default React.memo(function StepWrapper({
  stepKey,
  icon,
  title,
  description,
  extraText,
  children,
  ...props
}: StepWrapperProps) {
  const boxShadow = useColorModeValue('md', 'md-dark');
  const background = useBreakpointValue({ base: 'transparent', sm: 'bg-surface' });

  const animationVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  const {
    minH = '400px',
    width = '100%',
    ...rest
  } = props;

  return (
    <MotionBox
      key={stepKey}
      minH={minH}
      width={width}
      py={4}
      px={10}
      bg={background}
      boxShadow={boxShadow}
      borderRadius="xl"
      {...animationVariants}
      transition={{ duration: 0.3 }}
      {...rest}
    >
      <Stack gap={8}>
        <Stack gap={4} alignItems="center">
          <Flex w={16} h={16} align="center" justify="center" rounded="full" border="2px">
            <Icon w={8} h={8} as={icon} />
          </Flex>
          <Text fontSize="md">{title}</Text>
          {!!description && (
            <Text fontSize="sm" fontWeight="light">
            {description}
          </Text>)}
        </Stack>
        {children}
        {extraText && (
          <Text fontSize="xs" fontWeight="light">
            {extraText}
          </Text>
        )}
      </Stack>
    </MotionBox>
  );
});
